import { Link } from "react-router-dom";
import Reveal from "../components/Reveal";
import SectionLabel from "../components/SectionLabel";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { STYLE_SERIES } from "../data/content";

export default function Stilserien() {
  useDocumentTitle(
    "Stilserien — Küste, Alpin, Skandi, Landhaus Modern, Urban Retreat",
    "Fünf eigenständige Raumwelten für Ferienobjekte. Vollständig durchgearbeitete Gestaltungssysteme mit eigener Materialität, Palette und Haltung."
  );

  return (
    <div data-testid="page-stilserien">
      {/* Hero */}
      <section className="pt-32 md:pt-44 pb-20 md:pb-28">
        <div className="container-livinora">
          <Reveal>
            <SectionLabel index="∞" text="Stilserien" />
          </Reveal>
          <Reveal delay={60}>
            <h1 className="display-xl mt-8 text-[44px] sm:text-[60px] md:text-[80px] lg:text-[104px] text-balance max-w-5xl">
              Fünf Welten. Eine Haltung.
            </h1>
          </Reveal>
          <Reveal delay={140}>
            <p className="prose-livinora prose-livinora--lead mt-9">
              Jede Stilserie ist ein vollständig durchgearbeitetes Gestaltungssystem — mit eigener
              Materialität, eigener Farbstimmung und eigener Haltung. Wählen Sie nicht ein Möbel,
              sondern eine Welt.
            </p>
          </Reveal>
          <Reveal delay={220}>
            <div className="trust-strip mt-10" aria-label="Stilserien-Übersicht">
              {STYLE_SERIES.map((s) => (
                <span key={s.slug}>{s.name}</span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {STYLE_SERIES.map((s, i) => (
        <section
          key={s.slug}
          id={s.slug}
          className={`py-24 md:py-32 border-t border-[color:var(--livinora-hairline)] ${
            i % 2 === 1 ? "bg-[color:var(--livinora-paper-warm)]" : ""
          }`}
          data-testid={`stilserie-${s.slug}`}
        >
          <div className="container-livinora grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <div className={`lg:col-span-7 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
              <Reveal variant="mask">
                <div className="media-wrap aspect-[4/3]">
                  <img src={s.image} alt={s.name} className="w-full h-full object-cover" loading="lazy" />
                </div>
              </Reveal>
              <div className="mt-6 grid grid-cols-2 gap-4 md:gap-6">
                <Reveal variant="scale" delay={120}>
                  <div className="media-wrap aspect-[4/3]">
                    <img
                      src={s.imageDetail}
                      alt={`${s.name} Detail`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                </Reveal>
                <Reveal delay={180}>
                  <div className="card-depth p-6 flex flex-col justify-between h-full">
                    <div>
                      <span className="micro-label">Palette</span>
                      <div className="mt-3 flex gap-2">
                        {s.palette.map((c, idx) => (
                          <span
                            key={`${s.slug}-${c}-${idx}`}
                            className="w-7 h-7 sm:w-8 sm:h-8"
                            style={{ background: c, border: "1px solid var(--livinora-hairline)" }}
                            aria-label={c}
                          />
                        ))}
                      </div>
                    </div>
                    <div className="mt-6">
                      <span className="micro-label">Materialien</span>
                      <ul className="mt-3 text-sm space-y-1.5 text-[color:var(--livinora-ink)]">
                        {s.materials.map((m) => (
                          <li key={m} className="flex items-baseline gap-2.5">
                            <span className="w-2 h-px bg-[color:var(--livinora-stone-deep)] inline-block" />
                            {m}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </Reveal>
              </div>
            </div>

            <div className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
              <Reveal>
                <SectionLabel index={`0${i + 1}`} text="Stilserie" />
              </Reveal>
              <Reveal delay={60}>
                <h2 className="display-xl text-[44px] sm:text-6xl lg:text-7xl mt-7 text-balance">
                  {s.name}
                </h2>
              </Reveal>
              <Reveal delay={140}>
                <p className="mt-6 serif italic text-2xl text-[color:var(--livinora-ink-soft)] leading-snug">
                  {s.tagline}
                </p>
              </Reveal>
              <Reveal delay={220}>
                <p className="prose-livinora mt-7">{s.description}</p>
              </Reveal>
              <Reveal delay={300}>
                <div className="mt-10">
                  <span className="micro-label">Geeignet für</span>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {s.suitedFor.map((x) => (
                      <span
                        key={x}
                        className="text-[11px] uppercase tracking-[0.18em] px-3 py-2 border border-[color:var(--livinora-hairline)] text-[color:var(--livinora-ink-soft)]"
                      >
                        {x}
                      </span>
                    ))}
                  </div>
                </div>
              </Reveal>
              <Reveal delay={380}>
                <div className="mt-12 flex flex-wrap items-center gap-5">
                  <Link
                    to={`/konfigurator?style=${s.slug}`}
                    className="btn-livinora btn-primary btn-magnetic"
                  >
                    In Konfigurator wählen <span>→</span>
                  </Link>
                  <Link to={`/shop?style=${s.slug}`} className="btn-line">
                    Raumsets in {s.name}
                  </Link>
                </div>
              </Reveal>
            </div>
          </div>
        </section>
      ))}
    </div>
  );
}
